import { Client } from 'src/api/Client';
import {
  MediaTagsResponse,
  ContentPlanItem,
  Thought,
  ThoughtsResponse,
  CreateThoughtPayload,
  UpdateThoughtPayload,
} from './AiAgentClient.types';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
class AiAgentClient extends Client {
  private readonly Route = {
    MEDIA_TAGS: `${this.http.baseUrl}/ai-agent/media/tags`,
    CONTENT_PLAN: `${this.http.baseUrl}/ai-agent/content-ideas`,
    THOUGHTS_LIST: `${this.http.baseUrl}/thoughts`,
    THOUGHTS_CRUD: `${this.http.baseUrl}/thoughts`, // baseUrl already includes /api
    SAVE_INSPIRATION: `${this.http.baseUrl}/ai-agent/inspiration/save`,
  };

  private agentId: string | null = null;

  /**
   * Get the selected brand ID from cookie (same as BrandSelectionContext uses)
   */
  private getSelectedBrandId(): string | null {
    const BRAND_COOKIE_NAME = 'selectedBrandId';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${BRAND_COOKIE_NAME}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Gets the agent ID from localStorage
   * @returns Promise with the agent ID string
   */
  private async getAgentId(): Promise<string> {
    try {
      // If we already have an agent ID cached, return it
      if (this.agentId) {
        return this.agentId;
      }

      // Get from localStorage
      const cachedAgentId = localStorage.getItem('be_creatives_agent_id');
      if (cachedAgentId && cachedAgentId !== 'default-agent-id-for-testing') {
        this.agentId = cachedAgentId;
        return this.agentId;
      }

      // Development fallback - only use it if no cached ID is available
      if (process.env.NODE_ENV === 'development') {
        this.agentId = '60389db9-91dc-49de-aa85-7400c6a3ade6'; // Test agent ID
        return this.agentId;
      }

      throw new Error('No agent ID available. Please initialize the agent first.');
    } catch (error) {
      throw new Error('Failed to get agent ID. Please try again later.');
    }
  }

  /**
   * Get all available media tags
   * @returns Promise<MediaTagsResponse>
   */
  async getMediaTags(): Promise<MediaTagsResponse> {
    try {
      // Get agent_id for the request parameter
      const agentId = await this.getAgentId();

      // Build URL with agent_id parameter
      const url = `${this.Route.MEDIA_TAGS}?agent_id=${agentId}`;

      // Prepare headers with x-brand-id if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      // Add x-brand-id header if a brand is selected
      const selectedBrandId = this.getSelectedBrandId();
      if (selectedBrandId) {
        headers['x-brand-id'] = selectedBrandId;
      }

      // Add Authorization header
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      return this.http.get<MediaTagsResponse>(url, {
        headers,
      }) as unknown as Promise<MediaTagsResponse>;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get content plan items
   * @returns Promise<ContentPlanItem[]>
   */
  async getContentPlan(): Promise<ContentPlanItem[]> {
    try {
      // Get agent_id for the request parameter
      const agentId = await this.getAgentId();

      // Build URL with agent_id parameter
      const url = `${this.Route.CONTENT_PLAN}?agent_id=${agentId}`;

      // Prepare headers with x-brand-id if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      // Add x-brand-id header if a brand is selected
      const selectedBrandId = this.getSelectedBrandId();
      if (selectedBrandId) {
        headers['x-brand-id'] = selectedBrandId;
      }

      // Add Authorization header
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      const response = (await this.http.get(url, { headers })) as { data: ContentPlanItem[] };
      return response.data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all thoughts (ramblings) for the user
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise<ThoughtsResponse>
   */
  async getThoughts(
    page: number = 1,
    limit: number = 20,
    brandId?: string,
  ): Promise<ThoughtsResponse> {
    try {
      // Use brandId parameter if provided, otherwise get from cookie
      const selectedBrandId = brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }

      const url = `${this.Route.THOUGHTS_LIST}/${selectedBrandId}?page=${page}&limit=${limit}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      const response = (await this.http.get<ThoughtsResponse>(url, {
        headers,
      })) as unknown as ThoughtsResponse;

      return {
        ...response,
        total: Number(response.total) || 0,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a single thought by session ID
   * @param sessionId - The session ID
   * @returns Promise<Thought>
   */
  async getThought(sessionId: string): Promise<Thought> {
    try {
      const selectedBrandId = this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }

      const url = `${this.Route.THOUGHTS_LIST}/${selectedBrandId}/${sessionId}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      const response = (await this.http.get(url, { headers })) as { data: Thought };
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new thought
   * @param payload - The thought data
   * @returns Promise<Thought>
   */
  async createThought(payload: CreateThoughtPayload): Promise<Thought> {
    try {
      const selectedBrandId = this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }

      const url = `${this.Route.THOUGHTS_LIST}/${selectedBrandId}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      const response = (await this.http.post(url, payload, { headers })) as { data: Thought };
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a thought (rename)
   * @param sessionId - The session ID
   * @param payload - The update data
   * @param brandId - Optional brand ID
   * @returns Promise<Thought>
   */
  async updateThought(
    sessionId: string,
    payload: UpdateThoughtPayload,
    brandId?: string,
  ): Promise<Thought> {
    try {
      const selectedBrandId = brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }

      const url = `${this.Route.THOUGHTS_CRUD}/${selectedBrandId}/${sessionId}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      const response = (await this.http.put(url, payload, { headers })) as Thought;
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a thought
   * @param sessionId - The session ID
   * @param brandId - Optional brand ID
   * @returns Promise<void>
   */
  async deleteThought(sessionId: string, brandId?: string): Promise<void> {
    try {
      const selectedBrandId = brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }

      const url = `${this.Route.THOUGHTS_CRUD}/${selectedBrandId}/${sessionId}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      await this.http.delete(url, { headers });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Save inspiration video to Ben's media library with viral analysis
   * @param url - TikTok or Instagram URL
   * @param folder - Optional folder path (default: 'Inspirations')
   * @returns Promise with save result including mediaId, mediaUrl, videoInfo, etc.
   */
  async saveInspiration(
    url: string,
    folder: string = 'Inspirations',
  ): Promise<{
    success: boolean;
    /**
     * The video was already in the library, so nothing new was queued.
     *
     * Carried through because the backend answers a duplicate with `success: true` — callers
     * that only check `success` cannot tell "queued for you" from "you already had this" and
     * tell the user their video is on the way when it has been there all along.
     */
    duplicate?: boolean;
    mediaId?: string;
    mediaUrl?: string;
    sourceUrl: string;
    platform: 'tiktok' | 'instagram';
    analysisStatus: 'queued' | 'completed' | 'failed';
    message: string;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      // Add x-brand-id header if a brand is selected
      const selectedBrandId = this.getSelectedBrandId();
      if (selectedBrandId) {
        headers['x-brand-id'] = selectedBrandId;
      }

      // Add Authorization header
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }

      const response = await this.http.post<{
        success: boolean;
        duplicate?: boolean;
        jobId?: string;
        mediaId?: string;
        mediaUrl?: string;
        sourceUrl: string;
        platform: 'tiktok' | 'instagram';
        analysisStatus: 'queued' | 'completed' | 'failed';
        message: string;
      }>(this.Route.SAVE_INSPIRATION, { url, folder }, { headers });

      // Http interceptor returns response.data directly
      const data = response as any;

      // Validate response has required fields
      if (typeof data !== 'object' || data === null) {
        throw new Error('Invalid response format from server');
      }

      return {
        success: data.success ?? false,
        duplicate: data.duplicate,
        mediaId: data.mediaId,
        mediaUrl: data.mediaUrl,
        sourceUrl: data.sourceUrl ?? url,
        platform: data.platform ?? 'tiktok',
        analysisStatus: data.analysisStatus ?? 'queued',
        message: data.message ?? 'Request processed',
      };
    } catch (error) {
      throw error;
    }
  }
}

export { AiAgentClient };
